import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ArrowLeft, Boxes, CircleDollarSign, Package, Scale, WalletCards } from "lucide-react";
import LoadingIndicator from "../utils/loading/LoadingIndicator";
import { useLoading } from "../utils/loading/LoadingContext";
import "./styles/modern-pages.css";
import "./styles/palletview.css";

const PalletView = () => {
  const { palletNumber } = useParams();
  const navigate = useNavigate();
  const [pallet, setPallet] = useState(null);
  const [totalUnits, setTotalUnits] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [uniqueBOLs, setUniqueBOLs] = useState([]);
  const [totalRecovery, setTotalRecovery] = useState(0);
  const baseUrl = process.env.REACT_APP_DJANGO_API_BASE_URL;
  const { isLoading, setLoading } = useLoading();

  useEffect(() => {
    setLoading(true);
    fetch(`${baseUrl}/api/pallets/${palletNumber}/`)
      .then((response) => response.json())
      .then((data) => {
        setPallet(data);
        let unitsSum = 0;
        let costSum = 0;
        let weightSum = 0;
        let recoverySum = 0;

        data.items.forEach((item) => {
          unitsSum += item.Units || 0;
          costSum += parseFloat(item.TotalCost) || 0;
          weightSum += ((item.Units || 0) * (parseFloat(item.ItemPkgWeight) || 0)) / 1000;
          recoverySum += parseFloat(item.TotalRecovery) || 0;
        });

        setTotalUnits(unitsSum);
        setTotalCost(costSum);
        setTotalWeight(weightSum);
        setTotalRecovery(recoverySum);
        setUniqueBOLs([...new Set(data.items.map((item) => item.BOL))]);
      })
      .catch((error) => console.error("Error fetching pallet data:", error))
      .finally(() => setLoading(false));
  }, [palletNumber, baseUrl, setLoading]);

  const columns = useMemo(
    () => [
      { headerName: "Asin", field: "Asin" },
      { headerName: "Units", field: "Units" },
      { headerName: "Total Units Cost", field: "TotalCost" },
      { headerName: "Unit Recovery", field: "UnitRecovery" },
      { headerName: "Total Amount Recovered", field: "TotalRecovery" },
      {
        headerName: "Total Weight",
        valueGetter: (params) =>
          ((params.data.Units || 0) * (params.data.ItemPkgWeight || 0)) / 1000,
      },
      { headerName: "Removal Reason", field: "RemovalReason" },
      { headerName: "Item Description", field: "ItemDesc", minWidth: 260 },
    ],
    []
  );

  const statusBar = useMemo(
    () => ({
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "left", key: "totalRowComponent" },
        { statusPanel: "agAggregationComponent", align: "right" },
      ],
    }),
    []
  );

  if (isLoading) return <LoadingIndicator />;

  if (!pallet) {
    return (
      <div className="modern-page">
        <div className="modern-page-shell modern-panel modern-empty-state">
          No data available for this pallet.
        </div>
      </div>
    );
  }

  const summary = [
    { label: "Total units", value: totalUnits, icon: Boxes, color: "#2563eb" },
    { label: "Packets", value: uniqueBOLs.length, icon: Package, color: "#059669" },
    { label: "Total recovery", value: `${totalRecovery.toFixed(2)} €`, icon: WalletCards, color: "#0891b2" },
    { label: "Total cost", value: `${totalCost.toFixed(2)} €`, icon: CircleDollarSign, color: "#d97706" },
    { label: "Total weight", value: `${totalWeight.toFixed(2)} kg`, icon: Scale, color: "#7c3aed" },
  ];

  return (
    <div className="modern-page pallet-view-page">
      <div className="modern-page-shell">
        <header className="modern-page-header pallet-view-header">
          <div className="pallet-view-title">
            <button type="button" onClick={() => navigate(-1)} aria-label="Back to pallets">
              <ArrowLeft size={18} />
            </button>
            <h1 className="modern-page-title">Pallet {pallet.pallet_number}</h1>
          </div>
          <span className="modern-page-meta">{pallet.items.length} items</span>
        </header>

        <div className="pallet-summary-grid">
          {summary.map(({ label, value, icon: Icon, color }) => (
            <article className="pallet-summary-card" style={{ "--summary-accent": color }} key={label}>
              <span className="pallet-summary-icon"><Icon size={21} /></span>
              <div><strong>{value}</strong><span>{label}</span></div>
            </article>
          ))}
        </div>

        <section className="modern-panel pallet-items-panel">
          <div className="modern-panel-header"><h2>Items</h2></div>
          <div className="ag-theme-quartz pallet-items-grid">
            <AgGridReact
              rowData={pallet.items}
              columnDefs={columns}
              statusBar={statusBar}
              pagination
              domLayout="autoHeight"
              enableRangeSelection
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default PalletView;
